import { useEffect, useRef, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';

import { AUTH_TOKEN_KEY } from '../constants/auth';

export type PreviewStatus =
  | 'idle'
  | 'loading'
  | 'ready'
  | 'converting'
  | 'error';

export interface PreviewState {
  status: PreviewStatus;
  url?: string | null;
  errorCode?: string;
  errorMessage?: string;
}

const MAX_RETRIES = 10;
const DEFAULT_RETRY_MS = 1500;

const PREVIEW_STALE_TIME = 5 * 60 * 1000;
const PREVIEW_GC_TIME = 5 * 60 * 1000;

export interface PreviewFetchError {
  code: string;
  message?: string;
  status: number;
}

/**
 * Sleep for `ms`, but wake early once `signal` aborts — so unmounting (or a
 * refetch) during a 202 poll stops the next iteration promptly instead of
 * leaving a timer pending for the full retry interval. Rejects with the
 * signal's abort reason so the caller bubbles a genuine abort (which React
 * Query treats as a cancellation, not an error).
 */
function cancelableDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const step = Math.min(ms, 250);
    let remaining = ms;
    const id = setInterval(() => {
      remaining -= step;
      if (signal.aborted) {
        clearInterval(id);
        signal.removeEventListener('abort', onAbort);
        reject(signal.reason);
      } else if (remaining <= 0) {
        clearInterval(id);
        signal.removeEventListener('abort', onAbort);
        resolve();
      }
    }, step);
    const onAbort = () => {
      clearInterval(id);
      reject(signal.reason);
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

async function fetchPreviewBlob(
  documentId: string,
  version: number | undefined,
  signal: AbortSignal,
  onConverting: () => void,
): Promise<Blob> {
  let retryCount = 0;
  const url = `/api/v1/jerry/documents/${documentId}/preview${
    version != null ? `?version=${version}` : ''
  }`;
  const token =
    typeof localStorage !== 'undefined'
      ? (localStorage.getItem(AUTH_TOKEN_KEY) ?? '')
      : '';

  // eslint-disable-next-line no-constant-condition
  while (true) {
    // Pass the React Query-provided signal to fetch: an abort (unmount /
    // refetch / StrictMode remount) rejects with an AbortError, which React
    // Query recognises as a cancellation and does NOT surface as an error
    // state. This replaces the previous manual cancel flag, whose StrictMode
    // mount→unmount→remount cycle wrongly threw 'cancelled' and stuck the
    // preview in a failed state on first open.
    const resp = await fetch(url, {
      signal,
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    });

    if (resp.status === 202) {
      const data = await resp.json().catch(() => ({}));
      const retryAfter = data.retry_after_ms ?? DEFAULT_RETRY_MS;
      onConverting();
      retryCount += 1;
      if (retryCount > MAX_RETRIES) {
        const err: PreviewFetchError = {
          code: 'CONVERSION_TIMEOUT',
          message: '转换超时，请重试',
          status: 202,
        };
        throw err;
      }
      await cancelableDelay(retryAfter, signal);
      continue;
    }

    if (!resp.ok) {
      const errData = await resp.json().catch(() => ({ code: 'UNKNOWN' }));
      const err: PreviewFetchError = {
        code: errData.code ?? 'UNKNOWN',
        message: errData.message,
        status: resp.status,
      };
      throw err;
    }

    return resp.blob();
  }
}

export function useDocumentPreview(
  documentId: string | null | undefined,
  version?: number,
): { state: PreviewState; revokeUrl: () => void; retry: () => void } {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [convertingFlag, setConvertingFlag] = useState(false);
  const currentUrlRef = useRef<string | null>(null);

  const query = useQuery<Blob, PreviewFetchError | Error>({
    queryKey: ['document-preview', documentId, version ?? 'latest'],
    queryFn: ({ signal }) =>
      fetchPreviewBlob(
        documentId!,
        version,
        signal,
        () => setConvertingFlag(true),
      ),
    enabled: !!documentId,
    staleTime: PREVIEW_STALE_TIME,
    gcTime: PREVIEW_GC_TIME,
    retry: false,
  });

  const revokeUrl = useCallback(() => {
    if (currentUrlRef.current) {
      URL.revokeObjectURL(currentUrlRef.current);
      currentUrlRef.current = null;
    }
  }, []);

  // Re-run the preview fetch (used by the error state's retry button instead of
  // a full page reload). Clear the converting state first so the poll loop
  // starts fresh; React Query supplies a new AbortSignal for the refetch.
  const retry = useCallback(() => {
    setConvertingFlag(false);
    void query.refetch();
  }, [query]);

  // Manage object URL lifecycle from the fetched Blob.
  useEffect(() => {
    if (query.data) {
      revokeUrl();
      const objectUrl = URL.createObjectURL(query.data);
      currentUrlRef.current = objectUrl;
      setObjectUrl(objectUrl);
    } else if (!query.isLoading) {
      // Reset when there is no data and nothing in flight (e.g. disabled).
      if (objectUrl !== null) {
        revokeUrl();
        setObjectUrl(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.data, query.isLoading]);

  // Reset converting flag on new document / version. Revoke the object URL on
  // unmount / change to avoid leaking blob URLs. Cancellation of the in-flight
  // fetch is handled by React Query via the AbortSignal, not here.
  useEffect(() => {
    setConvertingFlag(false);
    return () => {
      revokeUrl();
    };
  }, [documentId, version, revokeUrl]);

  const state: PreviewState = (() => {
    if (!documentId) return { status: 'idle' };
    if (query.isError) {
      const err = query.error;
      if (err && typeof err === 'object' && 'code' in err) {
        const e = err as PreviewFetchError;
        return {
          status: 'error',
          errorCode: e.code,
          errorMessage: e.message,
        };
      }
      return {
        status: 'error',
        errorCode: 'UNKNOWN',
        errorMessage: err?.message,
      };
    }
    if (query.isSuccess) {
      return { status: 'ready', url: objectUrl };
    }
    // pending / fetching
    return { status: convertingFlag ? 'converting' : 'loading' };
  })();

  return { state, revokeUrl, retry };
}
